/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty,     jsonToSxProps,
    cssToSxProps,
    ifNotTransparent,
    ifNotNull } from '@olympeio/core';
import { useOlympeTheme } from './mui';
import { ThemeProvider } from '@mui/material/styles';
import React, {useEffect} from 'react';
import IButton from '@mui/material/IconButton';
import Icon from "@mui/material/Icon";
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { loadIconsSet } from "./icons";

/**
 * Provide a Button visual component using MUI Button
 */
export default class IconButton extends ReactBrick {
    /**
     * @override
     */
    setupExecution($: BrickContext) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    updateParent(parent: HTMLElement, element: HTMLElement) {
        parent.style.overflow = 'visible'; // Allow overflow
        return super.updateParent(parent, element);
    }

    /**
     * @override
     */
    static getReactComponent($: BrickContext) {
        return (props: { values: [hidden: boolean] }) => {
            const [hidden] = props.values;
            const theme = useOlympeTheme($);

            const icon = useProperty($, 'Icon', true);
            const style = useProperty($, 'Icon style', true);
            const iconSize = useProperty($, 'Icon size', true);
            const borderColor = useProperty($, 'Border Color', true);
            const borderRadius = useProperty($, 'Border Radius', true);
            const borderWidth = useProperty($, 'Border Width', true);
            const defaultColor = useProperty($, 'Default Color', true);
            const tooltip = useProperty($, 'Tooltip', true);
            const tooltipPosition = useProperty($, 'Tooltip Position', true);

            const customSx = {
                ...ifNotTransparent('color', useProperty($, 'Color', true)),
                ...ifNotTransparent('borderColor', borderColor),
                ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                ...ifNotNull('borderWidth', borderWidth),
                ...ifNotTransparent('borderStyle', 'solid', borderColor),
                ...ifNotTransparent('backgroundColor', defaultColor),
                '&:hover': {
                    ...ifNotTransparent('borderColor', borderColor),
                    ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                    ...ifNotNull('borderWidth', borderWidth),
                    ...ifNotTransparent('borderStyle', 'solid', borderColor),
                    ...ifNotTransparent('backgroundColor', defaultColor),
                },
            };

            useEffect(() => {
                loadIconsSet(style);
            }, [style])


            const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
                <Tooltip {...props} classes={{ popper: className }} />
            ))(({ theme }) => ({
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: theme.palette.secondary.dark,
                    color: 'white',
                    boxShadow: theme.shadows[1],
                    fontSize: 11,
                },
                [`& .${tooltipClasses.arrow}`]: {
                    "&:before": {
                        backgroundColor: theme.palette.secondary.dark,
                        boxShadow: theme.shadows[1],
                    }
                },
            }));

            return (
                !hidden && (
                    <ThemeProvider theme={theme}>
                        <LightTooltip title={tooltip} placement={tooltipPosition} arrow>
                            <IButton
                                disabled={useProperty($, 'Disabled', true)}
                                tabIndex={useProperty($, 'Tab Index', true)}
                                onClick={() => $.trigger('On Click')}
                                sx={{
                                    width: 1,
                                    height: 1,
                                    ...customSx,
                                    boxSizing: 'border-box',
                                    ...cssToSxProps(useProperty($, 'CSS Property', true)),
                                    ...jsonToSxProps(useProperty($, 'MUI sx', true)),
                                }}
                            >
                                <Icon baseClassName={'material-icons'+ (style === 'outlined' || style === 'two-tone' ? '-'+style : '')} sx={{fontSize: (iconSize ?? 24) + 'px !important', borderRadius: '5px'}}>{icon}</Icon>
                            </IButton>
                        </LightTooltip>
                    </ThemeProvider>
                )
            );
        };
    }
}

registerBrick('01917585c060729f1a38', IconButton);
